import React, { useEffect, useState, useCallback } from 'react';
import Header from '../components/layout/Header';
import { useAuth } from '../context/AuthContext';
import { getAuditLogs } from '../utils/api';
import { ShieldAlert, Search, RefreshCw, Building2, Briefcase, Trash2, Plus, Pencil } from 'lucide-react';
import toast from 'react-hot-toast';

const ACTION_CONFIG = {
  create_case:     { label: 'Case Created',       color: '#22c55e', bg: 'rgba(34,197,94,0.12)',    icon: Plus },
  delete_case:     { label: 'Case Deleted',        color: '#ef4444', bg: 'rgba(239,68,68,0.12)',    icon: Trash2 },
  create_provider: { label: 'Provider Added',      color: '#3b82f6', bg: 'rgba(59,130,246,0.12)',   icon: Plus },
  update_provider: { label: 'Provider Updated',    color: '#f59e0b', bg: 'rgba(245,158,11,0.12)',   icon: Pencil },
  delete_provider: { label: 'Provider Deleted',    color: '#ef4444', bg: 'rgba(239,68,68,0.12)',    icon: Trash2 },
};

const ALL_ACTIONS = Object.keys(ACTION_CONFIG);

function fmtDateTime(str) {
  if (!str) return '—';
  return new Date(str).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true,
  });
}

function fmtMeta(metadata) {
  if (!metadata) return null;
  try {
    const obj = typeof metadata === 'string' ? JSON.parse(metadata) : metadata;
    const parts = [];
    if (obj.changes) {
      Object.entries(obj.changes).forEach(([k, v]) => {
        const from = v.from != null ? String(v.from) : '—';
        const to   = v.to   != null ? String(v.to)   : '—';
        parts.push(`${k}: "${from}" → "${to}"`);
      });
    }
    if (obj.deleted_by)    parts.push(`Deleted by: ${obj.deleted_by}`);
    if (obj.provider_type) parts.push(`Type: ${obj.provider_type}`);
    if (obj.case_type)     parts.push(`Case type: ${obj.case_type}`);
    if (obj.reason)        parts.push(`Reason: ${obj.reason}`);
    return parts.length ? parts : null;
  } catch { return null; }
}

export default function AuditLogPage() {
  const { user } = useAuth();
  const isSuperAdmin = user?.access_level === 'super_admin';

  const [logs,       setLogs]       = useState([]);
  const [total,      setTotal]      = useState(0);
  const [loading,    setLoading]    = useState(true);
  const [search,     setSearch]     = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [typeFilter,   setTypeFilter]   = useState('');
  const [page,         setPage]         = useState(0);
  const LIMIT = 50;

  const load = useCallback(async () => {
    if (!isSuperAdmin) return;
    try {
      setLoading(true);
      const params = { limit: LIMIT, offset: page * LIMIT };
      if (actionFilter) params.action     = actionFilter;
      if (typeFilter)   params.target_type = typeFilter;
      const res = await getAuditLogs(params);
      setLogs(res.logs || []);
      setTotal(res.total || 0);
    } catch (err) {
      toast.error('Failed to load audit log');
    } finally { setLoading(false); }
  }, [isSuperAdmin, page, actionFilter, typeFilter]);

  useEffect(() => { load(); }, [load]);

  const filteredLogs = search.trim()
    ? logs.filter(l => {
        const q = search.toLowerCase();
        return (l.target_label || '').toLowerCase().includes(q) ||
               (l.admin_email  || '').toLowerCase().includes(q) ||
               (l.action       || '').toLowerCase().includes(q);
      })
    : logs;

  if (!isSuperAdmin) {
    return (
      <>
        <Header title="Audit Log" />
        <div className="app-content">
          <div className="empty-state">
            <ShieldAlert size={40} style={{ color: 'var(--status-red)' }} />
            <h3>Super Admin Access Required</h3>
            <p>Only Super Admins can view the Audit Log.</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Header title="Audit Log">
        <button className="btn btn-ghost btn-sm" onClick={load} disabled={loading}>
          <RefreshCw size={14} style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} /> Refresh
        </button>
      </Header>

      <div className="app-content">
        {/* Summary strip */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
          {Object.entries(ACTION_CONFIG).map(([action, cfg]) => {
            const count = logs.filter(l => l.action === action).length;
            return (
              <div
                key={action}
                onClick={() => setActionFilter(actionFilter === action ? '' : action)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 7, padding: '6px 12px',
                  borderRadius: 8, cursor: 'pointer', transition: 'all 0.12s',
                  background: actionFilter === action ? cfg.bg : 'var(--zeus-navy-light)',
                  border: `1px solid ${actionFilter === action ? cfg.color : 'var(--zeus-border)'}`,
                }}
              >
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: cfg.color, flexShrink: 0 }} />
                <span style={{ fontSize: '0.8rem', fontWeight: 600, color: actionFilter === action ? cfg.color : 'var(--text-secondary)' }}>
                  {cfg.label}
                </span>
                <span style={{ fontSize: '0.72rem', fontFamily: 'var(--font-mono)', color: 'var(--text-muted)' }}>
                  {count}
                </span>
              </div>
            );
          })}
        </div>

        {/* Toolbar */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
          <div className="search-bar" style={{ flex: 1, minWidth: 200 }}>
            <Search size={14} />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search by name, email, action..."
            />
          </div>
          <select className="form-select" style={{ width: 'auto' }}
            value={typeFilter} onChange={e => { setTypeFilter(e.target.value); setPage(0); }}>
            <option value="">All Types</option>
            <option value="case">Cases</option>
            <option value="provider">Providers</option>
          </select>
          {(actionFilter || typeFilter || search) && (
            <button className="btn btn-ghost btn-sm" onClick={() => { setActionFilter(''); setTypeFilter(''); setSearch(''); setPage(0); }}>
              Clear filters
            </button>
          )}
          <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginLeft: 'auto' }}>
            {total} event{total !== 1 ? 's' : ''} total
          </span>
        </div>

        {/* Table */}
        {loading ? (
          <div className="loading-spinner"><div className="spinner" /></div>
        ) : filteredLogs.length === 0 ? (
          <div className="empty-state">
            <ShieldAlert size={36} />
            <h3>No audit events</h3>
            <p>Audit events will appear here as cases and providers are created, updated, or deleted.</p>
          </div>
        ) : (
          <>
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th style={{ width: 160 }}>Date & Time</th>
                    <th style={{ width: 160 }}>Action</th>
                    <th>Target</th>
                    <th style={{ width: 180 }}>Performed By</th>
                    <th>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLogs.map((log, i) => {
                    const cfg   = ACTION_CONFIG[log.action] || { label: log.action, color: '#64748b', bg: 'rgba(100,116,139,0.1)', icon: ShieldAlert };
                    const Icon  = cfg.icon;
                    const meta  = fmtMeta(log.metadata);
                    const isCase = log.target_type === 'case';
                    return (
                      <tr key={log.id || i}>
                        <td style={{ fontSize: '0.8rem', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', whiteSpace: 'nowrap' }}>
                          {fmtDateTime(log.created_at)}
                        </td>
                        <td>
                          <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: 5,
                            padding: '3px 9px', borderRadius: 20, fontSize: '0.75rem', fontWeight: 700,
                            background: cfg.bg, color: cfg.color,
                          }}>
                            <Icon size={11} /> {cfg.label}
                          </span>
                        </td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                            {isCase
                              ? <Briefcase size={13} style={{ color: 'var(--zeus-gold)', flexShrink: 0 }} />
                              : <Building2 size={13} style={{ color: '#3b82f6', flexShrink: 0 }} />}
                            <span style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                              {log.target_label || '—'}
                            </span>
                          </div>
                        </td>
                        <td style={{ fontSize: '0.82rem', color: 'var(--text-secondary)' }}>
                          {log.admin_email || '—'}
                        </td>
                        <td>
                          {meta ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                              {meta.map((m, j) => (
                                <div key={j} style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
                                  {m}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <span style={{ color: 'var(--text-muted)', fontSize: '0.78rem' }}>—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {total > LIMIT && (
              <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 16 }}>
                <button className="btn btn-ghost btn-sm" disabled={page === 0} onClick={() => setPage(p => p - 1)}>← Prev</button>
                <span style={{ fontSize: '0.82rem', color: 'var(--text-muted)', alignSelf: 'center' }}>
                  Page {page + 1} of {Math.ceil(total / LIMIT)}
                </span>
                <button className="btn btn-ghost btn-sm" disabled={(page + 1) * LIMIT >= total} onClick={() => setPage(p => p + 1)}>Next →</button>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}
